<template>
    <div class="contracts-template-record" :key="updateKey" :class="{ 'has-floating-bar': showStickyBar }">
        <FullPageLoader :isLoading="loading"/>

        <ContractTemplateSignatureModal
            :record="record"
            :show="signatureModal"
            :index="signatureBlock"
            @passSignaturePath="passSignaturePath"
            @close="signatureModal = false"
        />

        <div class="row">
            <div class="column medium-3 text-right float-right">
                <b-button tag="router-link" :to="{ name: 'ContractsTemplates' }" type="is-dark">Terug naar overzicht</b-button>
            </div>
            <div class="column small-9">
                <h1>
                    Contract aanpassen:&nbsp;{{ templateTypeLabel(record.type) }}
                </h1>
            </div>
        </div>

        <div class="row">
            <div class="column">
                <form class="form verticalpadding">
                    <div class="row">
                        <div class="column large-6">
                            <h3>Bewerk instructies</h3>
                        </div>

                        <div class="column large-6 text-right" v-if="!hideSaving">
                            <b-button type="is-success" icon-right="file-pdf" @click="previewPDF">{{ previewButtonText }}</b-button>

                            <b-dropdown :triggers="['hover']" position="is-bottom-left" aria-role="list">
                                <button class="button is-primary" slot="trigger" @click.prevent="">
                                    <span>Blok toevoegen</span>
                                    <b-icon icon="angle-down"></b-icon>
                                </button>

                                <b-dropdown-item aria-role="listitem" @click="addContent('titleAndContent')">Titel en content</b-dropdown-item>
                                <b-dropdown-item aria-role="listitem" @click="addContent('signatureCustomer')">Handtekening (enkel huurder)</b-dropdown-item>
                                <b-dropdown-item aria-role="listitem" @click="addContent('signatureCustomerAndOwner')">Handtekening (huurder en verhuurder)</b-dropdown-item>
                                <b-dropdown-item aria-role="listitem" @click="addContent('pagebreak')">Pagina einde</b-dropdown-item>
                            </b-dropdown>
                        </div>

                        <div class="column">
                            <p>
                                <ul>
                                    <li><strong>Herschikken van contentblokken:</strong> Sleep de zwarte knop met de strepen om de indeling van de contentblokken aan te passen.</li>
                                    <li><strong>Toevoegen van handtekeningvelden:</strong> Je kunt eenvoudig handtekeningvelden toevoegen waar nodig, hierbij is het mogelijk om een standaard handtekening van de verhuurder toe te voegen.</li>
                                    <li><strong>Pagina's indelen:</strong> Wil je content op een nieuwe pagina plaatsen? Voeg dan een pagina-einde toe.</li>
                                </ul>
                            </p>

                            <div v-if="record && record.type == 'agreement'">
                                <h3>Hulpwoorden</h3>
                                <p>Om de tekst in het contract dynamisch te maken is de mogelijkheid om hulpwoorden in de tekst te plaatsen.</p>

                                <table>
                                    <thead>
                                        <tr>
                                            <th>Hulpwoord</th>
                                            <th>Uitleg</th>
                                            <th>Voorbeeld</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <th><code>{bedrijfsnaam}</code></th>
                                            <th>Bedrijfsnaam van de organisatie.</th>
                                            <th>Mijn Organisatie B.V.</th>
                                        </tr>
                                        <tr>
                                            <th><code>{contract_startdatum}</code></th>
                                            <th>Datum wanneer het contract ingaat.</th>
                                            <th>{{ moment().format('DD-MM-Y') }}</th>
                                        </tr>
                                        <tr>
                                            <th><code>{contract_einddatum}</code></th>
                                            <th>Datum wanneer het contract eindigt.</th>
                                            <th>{{ moment().add(1, 'year').format('DD-MM-Y') }} of (niet van toepassing).</th>
                                        </tr>
                                        <tr>
                                            <th><code>{factuur_frequentie}</code></th>
                                            <th>Hoe vaak de facturen worden verstuurd.</th>
                                            <th>maandelijkse facturatie<br>of: (1-5) wekelijkse facturatie, kwartaal facturatie, halfjaar facturatie, jaar facturatie</th>
                                        </tr>
                                        <tr>
                                            <th><code>{bedrijfsadres}</code></th>
                                            <th>Adres van de organisatie.</th>
                                            <th>Straat 123, Plaatsnaam</th>
                                        </tr>
                                        <tr>
                                            <th><code>{totaalprijs}</code></th>
                                            <th>Totale prijs in contract (incl. euroteken).</th>
                                            <th>€&nbsp;125,00</th>
                                        </tr>
                                        <tr>
                                            <th><code>{totaalborg}</code></th>
                                            <th>Totale borg in contract (incl. euroteken).</th>
                                            <th>€&nbsp;125,00</th>
                                        </tr>
                                        <tr v-if="user.organisation_type == 'box'">
                                            <th><code>{box_nummers}</code></th>
                                            <th>Boxnummer(s).</th>
                                            <th>100 - 100 en 200 - 100, 200 en 300</th>
                                        </tr>
                                    </tbody>
                                </table>

                                <p><small>Meer dynamische gegevens nodig in je contract? Neem contact met ons op via <a href="mailto:info@planiteasy.nl">info@planiteasy.nl</a>.</small></p>
                            </div>
                            <div v-else-if="record && record.type == 'addendum'">
                                <h3>Hulpwoorden</h3>
                                <p>Gebruik onderstaande placeholders in je content om contractaanpassing gegevens dynamisch te tonen.</p>
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Placeholder</th>
                                            <th>Uitleg</th>
                                            <th>Voorbeeld</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <th><code>{{ addendumPlaceholders.contract_nummer_addendum }}</code></th>
                                            <th>Referentie van de contractaanpassing.</th>
                                            <th>20260010-01</th>
                                        </tr>
                                        <tr>
                                            <th><code>{{ addendumPlaceholders.contract_nummer }}</code></th>
                                            <th>Contractnummer waar de wijziging op hoort.</th>
                                            <th>20260010</th>
                                        </tr>
                                        <tr>
                                            <th><code>{{ addendumPlaceholders.datum }}</code></th>
                                            <th>Ingangsdatum van de contractaanpassing.</th>
                                            <th>{{ moment().format('DD-MM-Y') }}</th>
                                        </tr>
                                        <tr>
                                            <th><code>{{ addendumPlaceholders.wijzigingen_tabel }}</code></th>
                                            <th>HTML tabel met alle wijzigingen (toegevoegd, verwijderd, aangepast).</th>
                                            <th>Tabel met wijzigingen</th>
                                        </tr>
                                        <tr>
                                            <th><code>{{ addendumPlaceholders.opmerking }}</code></th>
                                            <th>Optionele opmerking uit de contractaanpassing.</th>
                                            <th>Klant wil per 01-06 overstappen naar box 205.</th>
                                        </tr>
                                        <tr>
                                            <th><code>{{ addendumPlaceholders.facturatie_frequentie }}</code></th>
                                            <th>Facturatiefrequentie die geldt na verwerken van de contractaanpassing.</th>
                                            <th>Kwartaal</th>
                                        </tr>
                                        <tr>
                                            <th><code>{{ addendumPlaceholders.betaalmethode }}</code></th>
                                            <th>Betaalmethode die geldt na verwerken van de contractaanpassing.</th>
                                            <th>Handmatig</th>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    <div v-if="record && (record.type == 'agreement' || record.type == 'addendum')">
                        <hr>
                    </div>

                    <div class="row template-controls">
                        <div class="column">
                            <b-field label="Document titel"
                                :message="getError(errors, 'title')"
                                :type="getType(errors, 'title', '')"
                                style="pointer-events: none"
                            >
                                <b-input v-model="record.title" placeholder="Titel" style="pointer-events: auto"></b-input>
                            </b-field>
                        </div>

                        <div class="column medium-12">
                            <b-field label="Template type">
                                <b-switch v-model="record.split"
                                    true-value="1"
                                    false-value="0"
                                    @input="handleSplitChange">
                                    Aparte versie voor zakelijke klanten
                                </b-switch>
                            </b-field>
                        </div>

                        <div v-if="record.split == '1'" class="column medium-6 end">
                            <b-field label="Template versie">
                                <b-select v-model="activeVersion" expanded @change="handleVersionChange">
                                    <option value="normal">Particulier</option>
                                    <option value="business">Zakelijk</option>
                                </b-select>
                            </b-field>
                        </div>
                    </div>
                </form>

                <form class="form content-editor verticalpadding"
                :class="{
                    'business-version': record.split == '1' && activeVersion === 'business',
                    'normal-version': record.split == '1' && activeVersion === 'normal'
                }">
                    <div class="row">
                        <div class="column" v-if="record.split == '1'">
                            <h3>{{ activeVersion == 'business' ? 'Zakelijke versie' : 'Particuliere versie' }}</h3>
                        </div>
                        <div class="column">
                            <div class="draggable">
                                <transition name="fade" mode="out-in">
                                    <draggable :list="activeContent" handle=".handle" :key="activeVersion">
                                        <div v-for="(chapter, index) in activeContent" :key="'part_'+index" class="drag-me">
                                            <div class="column toolbar">
                                                <div class="row">
                                                    <div class="column small-8">
                                                        <h5>{{ index+1 }}. {{ chapter.typename }}</h5>
                                                    </div>
                                                    <div class="column small-4 text-right">
                                                        <b-tooltip label="Sleep om te herschikken" position="is-left">
                                                            <b-button type="is-dark" class="handle" icon-right="bars" v-if="!hideSaving"></b-button>
                                                        </b-tooltip>
                                                        <b-button type="is-danger" icon-right="times" v-show="chapter.deletable && !hideSaving" @click="deleteChapter(index)">
                                                            Verwijderen
                                                        </b-button>
                                                    </div>
                                                </div>
                                            </div>

                                            <div v-if="chapter.type == 'signatureCustomer' || chapter.type == 'signatureCustomerAndOwner'">
                                                <img :src="`/images/signature_${chapter.type}.jpg`" alt="handtekening" style="padding:15px;">

                                                <div v-if="_.has(chapter, 'orgSignature')">
                                                    <b-notification type="is-success" role="alert">
                                                        <b-icon icon="check" style="display: inline-block"/>&nbsp;Standaard handtekening toegevoegd.
                                                    </b-notification>
                                                </div>
                                                <b-notification v-else-if="chapter.type == 'signatureCustomerAndOwner' && !_.has(chapter, 'orgSignature') && !_.isNull(record.organisation)" type="is-warning">
                                                    Geen handtekening aanwezig. Voeg het "Handtekening (huurder en verhuurder)"-blok opnieuw toe om een handtekening in te stellen.
                                                </b-notification>
                                            </div>

                                            <div v-if="chapter.type == 'pagebreak'">
                                                <hr>
                                            </div>

                                            <div class="column" v-if="hasEditableChapterTitle(chapter)">
                                                <b-field label="Titel">
                                                    <b-input v-model="chapter.title" placeholder="Titel"></b-input>
                                                </b-field>
                                            </div>
                                            <div class="column" v-if="chapter.hasHTMLeditor">
                                                <QuillEditor
                                                    :theContent="chapter.content"
                                                    :index="index"
                                                    @contentChanged="contentChanged"
                                                    :toolbar="quillToolbarOptions"
                                                />
                                            </div>
                                        </div>
                                    </draggable>
                                </transition>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>

        <div class="row" v-if="!hideSaving">
            <div class="column">
                <div class="formfooter-wrapper" :class="{ 'is-floating': showStickyBar }">
                    <div class="row">
                        <div class="formfooter">
                            <div class="left-controls">
                                <b-dropdown :triggers="['hover']" position="is-top-right" aria-role="list">
                                    <button class="button is-primary" slot="trigger" @click.prevent="">
                                        <span>Blok toevoegen</span>
                                        <b-icon icon="angle-down"></b-icon>
                                    </button>

                                    <b-dropdown-item aria-role="listitem" @click="addContent('titleAndContent')">Titel en content</b-dropdown-item>
                                    <b-dropdown-item aria-role="listitem" @click="addContent('signatureCustomer')">Handtekening (enkel huurder)</b-dropdown-item>
                                    <b-dropdown-item aria-role="listitem" @click="addContent('signatureCustomerAndOwner')">Handtekening (huurder en verhuurder)</b-dropdown-item>
                                    <b-dropdown-item aria-role="listitem" @click="addContent('pagebreak')">Pagina einde</b-dropdown-item>
                                </b-dropdown>

                                <div v-if="record.split == '1'" class="version-switcher">
                                    <b-select v-model="activeVersion" @change="handleVersionChange">
                                        <option value="normal">Particuliere versie</option>
                                        <option value="business">Zakelijke versie</option>
                                    </b-select>
                                </div>

                                <b-button type="is-success" icon-right="file-pdf" @click="previewPDF">{{ previewButtonText }}</b-button>
                            </div>
                            <div class="right-controls">
                                <b-button type="is-success" :loading="loading" @click="saveRecord">Opslaan</b-button>
                                <b-button type="is-dark" :loading="loading" @click="saveAndQuit">Opslaan en sluiten</b-button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import QuillEditor from '../QuillEditor'
import draggable from 'vuedraggable'
import formvalidationMixin from '@/mixins/formvalidationMixin.js'
import ContractTemplateSignatureModal from './ContractTemplateSignatureModal'
import toastMixins from '@/mixins/toastMixins.js'

export default {
    name: 'ContractsTemplateRecord',

    components: {
        QuillEditor,
        draggable,
        ContractTemplateSignatureModal
    },

    mixins: [formvalidationMixin, toastMixins],

    data() {
        return {
            updateKey: 0,
            loading: false,
            record: {},
            content: [],
            content_business: [],
            activeVersion: 'normal',
            errors: {},
            // Signature
            signatureModal: false,
            signatureBlock: null,
            showStickyBar: false,
            lastScrollPosition: 0,
            resizeObserver: null,
            quillToolbarOptions: [
                ['bold', 'italic', 'underline', 'strike'],
                ['blockquote'],
                [{ list: 'ordered' }, { list: 'bullet' }],
                [{ header: [1, 2, 3, 4, 5, 6, false] }],
                [{ color: [] }]
            ]
        }
    },

    computed: {
        user() {
            return this.$store.state.user
        },

        hideSaving() {
            let hideSaving = false

            if(_.has(this.user.organisation_settings, 'strictroles') && this.user.organisation_settings.strictroles == 1 && (this.user.role != 'pit' && !this.user.is_admin)){
                hideSaving = true
            }

            return hideSaving
        },

        activeContent: {
            get() {
                if (this.record.split == '1' && this.activeVersion === 'business') {
                    return this.content_business
                }
                return this.content
            },
            set(value) {
                if (this.record.split == '1' && this.activeVersion === 'business') {
                    this.content_business = value
                } else {
                    this.content = value
                }
            }
        },

        previewButtonText() {
            if (this.record.split == '1') {
                if (this.activeVersion === 'business') {
                    return 'Voorbeeld PDF (zakelijk)';
                } else {
                    return 'Voorbeeld PDF (particulier)';
                }
            } else {
                return 'Voorbeeld PDF';
            }
        },

        templateTypeLabel() {
            return (type) => {
                if (type === 'agreement') return 'Overeenkomst'
                if (type === 'general') return 'Algemeen'
                if (type === 'addendum') return 'Contractaanpassing'
                return type
            }
        },

        addendumPlaceholders() {
            return {
                contract_nummer_addendum: '{{ contract_nummer_addendum }}',
                contract_nummer: '{{ contract_nummer }}',
                datum: '{{ datum }}',
                wijzigingen_tabel: '{{ wijzigingen_tabel }}',
                opmerking: '{{ opmerking }}',
                facturatie_frequentie: '{{ facturatie_frequentie }}',
                betaalmethode: '{{ betaalmethode }}'
            }
        }
    },

    created () {
        this.fetchRecord()
    },

    mounted() {
        window.addEventListener('scroll', this.handleScroll)

        // Create a ResizeObserver to watch for content height changes
        this.resizeObserver = new ResizeObserver(() => {
            this.handleScroll()
        })

        // Observe the content container
        const contentEditor = document.querySelector('.content-editor')
        if (contentEditor) {
            this.resizeObserver.observe(contentEditor)
        }
    },

    beforeDestroy() {
        window.removeEventListener('scroll', this.handleScroll)
        if (this.resizeObserver) {
            this.resizeObserver.disconnect()
        }
    },

    methods: {
        hasEditableChapterTitle(chapter) {
            return _.has(chapter, 'title') && typeof chapter.title === 'string'
        },

        fetchRecord() {
            this.loading = true

            axios.get('/api/contract-templates/'+this.$route.params.id, {
                headers: {'Authorization': 'bearer ' + this.$store.state.authToken}
            }).then((response) => {
                this.content = JSON.parse(response.data.content)
                if (response.data.content_business) {
                    this.content_business = JSON.parse(response.data.content_business)
                }
                this.record = response.data
            }).catch((error) => {
                console.error('[❌] Error while fetching contract template', error)
                this.showError('Geen toegang!')
                this.$router.push({name: 'ContractsTemplates'})
            }).then(() => {
                this.loading = false
            })
        },

        handleSplitChange(value) {
            if (value === '1') {
                this.$buefy.dialog.confirm({
                    title: 'Template splitsen',
                    message: 'Wil je een aparte versie maken voor zakelijke klanten? De huidige template wordt gekopieerd als basis voor de zakelijke versie.',
                    confirmText: 'Ja, splitsen',
                    cancelText: 'Annuleren',
                    type: 'is-info',
                    onConfirm: () => {
                        if (!this.record.content_business) {
                            // Copy regular content to business content when splitting for the first time
                            this.content_business = JSON.parse(JSON.stringify(this.content))
                        }
                        this.record.split = '1'
                        this.activeVersion = 'business'
                    },
                    onCancel: () => {
                        this.record.split = '0'
                    }
                })
            } else if (value === '0') {
                // Show merge confirmation when turning off split
                this.$buefy.dialog.confirm({
                    title: 'Template verwijderen',
                    message: 'Weet je zeker dat je de zakelijke versie wilt verwijderen en terug wilt naar één template voor beide type klanten?',
                    confirmText: 'Ja, verwijderen',
                    cancelText: 'Annuleren',
                    type: 'is-info',
                    onConfirm: () => {
                        this.record.split = '0'
                        this.content_business = null
                        this.activeVersion = 'normal'
                    },
                    onCancel: () => {
                        this.record.split = '1'
                    }
                })
            }
        },

        saveAndQuit(){
            this.loading = true

            this.save().then(() => {
                this.$router.push({name: 'ContractsTemplates'})
            }).catch((e) => {
                if (e) {
                    this.showError(e)
                }
            }).finally(() => {
                this.loading = false
            })
        },

        saveRecord(){
            this.loading = true

            this.save().then(() => {
                this.showMessage('Succesvol opgeslagen!')
            }).catch((e) => {
                if (e) {
                    this.showError(e)
                }
            }).finally(() => {
                this.loading = false
            })
        },

        save() {
            this.record.content = JSON.stringify(this.content)

            if (this.record.split == '1') {
                this.record.content_business = JSON.stringify(this.content_business)
            } else {
                this.record.content_business = null
            }

            return new Promise((resolve, reject) => {
                axios.post('/api/contract-templates/'+this.$route.params.id, {...this.record, activeVersion: this.activeVersion}, {
                    headers: {'Authorization': 'bearer ' + this.$store.state.authToken}
                }).then((response) => {
                    this.showMessage('Succesvol opgeslagen!')
                    resolve(response.data)
                }).catch((error) => {
                    if(_.has(error, 'response.data.error') && error.response.data.error === 'not-org-admin'){
                        reject('Opslaan niet mogelijk, enkel een administrator is gemachtigd om deze gegevens aan te passen.')
                    } else {
                        this.showError('Opslaan mislukt - Onvoldoende of onjuiste gegevens.')
                    }

                    this.errors = {...error.response.data.errors}
                    this.loading = false

                    reject()
                })
            })
        },

        addContent(type){
            let contentObject = false
            let signaturePossible = false;

            switch(type) {
                case 'titleAndContent':
                    contentObject = {
                        'type': 'titleAndContent',
                        'typename': 'Titel en content',
                        'title': '',
                        'deletable': true,
                        'hasHTMLeditor': true,
                        'content': '',
                    }
                break;
                case 'signatureCustomer':
                    contentObject = {
                        'type': 'signatureCustomer',
                        'typename': 'Handtekening huurder',
                        'title': false,
                        'deletable': true,
                        'hasHTMLeditor': false,
                    }
                break;
                case 'signatureCustomerAndOwner':
                    signaturePossible = true
                    contentObject = {
                        'type': 'signatureCustomerAndOwner',
                        'typename': 'Handtekening huurder en verhuurder',
                        'title': false,
                        'deletable': true,
                        'hasHTMLeditor': false,
                    }
                break;
                case 'pagebreak':
                    contentObject = {
                        'type': 'pagebreak',
                        'typename': 'Pagina einde (einde pagina)',
                        'deletable': true
                    }
                break;
            }

            if(contentObject) {
                if (this.record.split == '1' && this.activeVersion === 'business') {
                    this.content_business.push(contentObject)
                } else {
                    this.content.push(contentObject)
                }

                this.showMessage('Blok toegevoegd!')

                if (signaturePossible) {
                    this.signatureModal = true
                    this.signatureBlock = this.activeContent.length - 1
                }

                // Recalculate scroll position after content change
                this.$nextTick(() => {
                    this.handleScroll()
                })
            } else {
                console.error(`Type ${type} is not defined!`)
            }
        },

        deleteChapter(index) {
            if (this.record.split == '1' && this.activeVersion === 'business') {
                this.content_business.splice(index, 1)
            } else {
                this.content.splice(index, 1)
            }

            // Recalculate scroll position after content deletion
            this.$nextTick(() => {
                this.handleScroll()
            })
        },

        contentChanged(data){
            if (this.record.split == '1' && this.activeVersion === 'business') {
                this.content_business[data.index].content = data.html
            } else {
                this.content[data.index].content = data.html
            }

            // Recalculate scroll position after content edit
            this.$nextTick(() => {
                this.handleScroll()
            })
        },

        previewPDF(){
            this.loading = true

            const sendData = {
                ...this.record,
                content: this.activeVersion === 'normal' ?JSON.stringify(this.content) : null,
                content_business: this.activeVersion == 'business' ? JSON.stringify(this.content_business) : null,
                activeVersion: this.activeVersion
            }

            axios.post('/api/contract-templates/'+this.$route.params.id+'/preview', sendData, {
                headers: {'Authorization': 'bearer ' + this.$store.state.authToken}
            }).then((response) => {
                window.open('http://'+response.data, '_blank')
            }).catch((e) => {
                this.showError('Fout bij het genereren van de PDF')
            }).finally(() => {
                this.loading = false
            })
        },

        passSignaturePath(path) {
            if (this.record.split == '1' && this.activeVersion === 'business') {
                this.content_business[this.signatureBlock].orgSignature = path
            } else {
                this.content[this.signatureBlock].orgSignature = path
            }
            this.updateKey = this.updateKey + 1
        },

        handleVersionChange(newVersion) {
            this.loading = true
            setTimeout(() => {
                this.activeVersion = newVersion
                this.loading = false
                // Recalculate scroll position after version change
                this.$nextTick(() => {
                    this.handleScroll()
                })
            }, 100)
        },

        handleScroll() {
            // Force recalculation of document height
            const documentHeight = Math.max(
                document.body.scrollHeight,
                document.documentElement.scrollHeight,
                document.body.offsetHeight,
                document.documentElement.offsetHeight,
                document.body.clientHeight,
                document.documentElement.clientHeight
            )

            const scrollPosition = window.scrollY
            const windowHeight = window.innerHeight
            const distanceFromBottom = documentHeight - (scrollPosition + windowHeight)

            this.showStickyBar = scrollPosition > 300 &&
                (distanceFromBottom > 150 || (this.lastScrollPosition > scrollPosition && distanceFromBottom < 150))

            this.lastScrollPosition = scrollPosition
        }
    }
}
</script>

<style lang="scss" scoped>
#panel {
    table{width:100%;
        thead{
            tr{
                th{padding:10px;}
            }
        }
        tbody{
            tr{
                th{font-weight:400;border: 1px solid #ddd;padding: 10px; font-size: 14px;}
            }
        }
    }

    .content-editor {
        margin-top: 2rem;
    }

    .business-version {
        border-left: 4px solid #3273dc;
        padding: 1rem;
        position: relative;

        .toolbar {
            background-color: #e6f7ff;
        }
    }

    .normal-version {
        border-left: 4px solid #ff9f43;
        padding: 1rem;
        position: relative;

        .toolbar {
            background-color: #fff4e6;
        }
    }

    .draggable {
        position: relative;
        min-height: 100px; // Ensure container has height even when empty
    }

    .fade-enter-active,
    .fade-leave-active {
        transition: opacity 0.2s ease;
        position: absolute;
        width: 100%;
        top: 0;
        left: 0;
    }

    .fade-enter-from,
    .fade-leave-to {
        opacity: 0;
    }

    .fade-enter-to,
    .fade-leave-from {
        opacity: 1;
    }

    // Add this to ensure the new content is positioned correctly after transition
    .fade-enter-to {
        position: relative;
    }

    .formfooter-wrapper {
        width: 100%;
        transition: bottom 0.3s ease;
        bottom: -5rem;

        .row {
            margin: 0 auto;
        }

        .formfooter {
            background: white;
            border-radius: 8px;
            border: 1px solid #dbdbdb;
        }

        &.is-floating {
            position: fixed;
            bottom: 1rem;
            left: 250px;
            right: 0;
            z-index: 30;
            width: calc(100% - 250px);

            .formfooter {
                box-shadow: 0px 15px 50px rgba(#000, 0.5);
                margin: 0 15px;
            }

            @media (max-width: 1024px) {
                left: 0;
                width: 100%;
            }
        }
    }

    .formfooter {
        display: flex;
        justify-content: space-between;
        align-items: center;
        overflow: inherit;

        .left-controls {
            display: flex;
            align-items: center;
            gap: 1rem;

            .version-switcher {
                display: flex;
                align-items: center;
                gap: 1rem;

                .version-label {
                    font-weight: 600;
                    color: #363636;
                }

                .select {
                    min-width: 200px;
                }
            }

            @media (max-width: 768px) {
                display: none;
            }
        }

        .right-controls {
            display: flex;
            gap: 0.5rem;
        }


        @media (max-width: 768px) {
            justify-content: flex-end;
        }
    }

    .contracts-template-record {
        &.has-floating-bar {
            padding-bottom: calc(73px + 2rem); // Height of the formfooter (48px) + padding (25px) + extra margin
        }
    }
}
</style>
